import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Filter } from 'lucide-react';
import DateRangePicker from '../components/dashboard/DateRangePicker';
import RevenueChart from '../components/dashboard/RevenueChart';
import DataTable from '../components/dashboard/DataTable';
import FilterPanel from '../components/dashboard/FilterPanel';
import AutoRefreshControl from '../components/dashboard/AutoRefreshControl';
import { analyticsService } from '../services/analyticsService';
import { getDefaultDateRange } from '../utils/config';
import SummaryCard from '../components/dashboard/SummaryCard';
import { DollarSign, Eye, MousePointerClick, TrendingUp, Users, Activity, Apple, Smartphone, UserCheck, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

/**
 * Analytics Dashboard Page
 * Displays detailed analytics with filters and data breakdown
 */
const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [breakdownType, setBreakdownType] = useState('app'); // 'app', 'ad_unit', 'country'
  const [filterOptions, setFilterOptions] = useState({
    apps: [],
    countries: [],
    adUnits: [],
    adSources: [],
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60000); // 1 minute default
  const refreshIntervalRef = useRef(null);

  // Date range state (defaults to current month, 1st to today)
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());

  // Filter state
  const [filters, setFilters] = useState({
    appKey: [],
    country: [],
    adUnit: [],
    adSource: [],
  });

  // Account ID state (optional, defaults to user's default account)
  const [accountId, setAccountId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Memoize table columns (must be at top level, not conditional)
  const tableColumns = useMemo(() => [
    {
      accessorKey: 'breakdown_value',
      header: breakdownType === 'app' ? 'App' : breakdownType === 'ad_unit' ? 'Ad Unit' : breakdownType === 'ad_source' ? 'Ad Source' : 'Country',
      cell: ({ row, getValue }) => {
        const value = getValue();
        // Show platform icon for app breakdown
        if (breakdownType === 'app' && row.original.platform) {
          const platform = row.original.platform;
          return (
            <div className="flex items-center gap-2">
              {platform === 'iOS' ? (
                <Apple className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Smartphone className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="font-medium">{value}</span>
            </div>
          );
        }
        return <span className="font-medium">{value}</span>;
      },
    },
    {
      accessorKey: 'total_revenue',
      header: 'Revenue',
      cell: ({ getValue }) => {
        const value = getValue();
        return typeof value === 'number'
          ? (value >= 1000000
            ? `$${(value / 1000000).toFixed(2)}M`
            : value >= 1000
              ? `$${(value / 1000).toFixed(2)}K`
              : `$${value.toFixed(2)}`)
          : value;
      },
    },
    {
      accessorKey: 'total_impressions',
      header: 'Impressions',
      cell: ({ getValue }) => {
        const value = getValue();
        return typeof value === 'number'
          ? (value >= 1000000
            ? `${(value / 1000000).toFixed(1)}M`
            : value >= 1000
              ? `${(value / 1000).toFixed(1)}K`
              : value.toLocaleString())
          : value;
      },
    },
    {
      accessorKey: 'average_ecpm',
      header: 'eCPM',
      cell: ({ getValue }) => {
        const value = getValue();
        return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
      },
    },
  ], [breakdownType]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      };

      if (accountId) {
        params.account_id = accountId;
      }

      // Fetch all filter options in parallel
      const [appsResponse, adUnitsResponse, countriesResponse, adSourcesResponse] = await Promise.all([
        analyticsService.getTopApps({ ...params, limit: 100 }),
        analyticsService.getTopAdUnits({ ...params, limit: 100 }),
        analyticsService.getTopCountries({ ...params, limit: 100 }),
        analyticsService.getRevenueSummary({ ...params, breakdown: 'ad_source', limit: 100 }),
      ]);

      setFilterOptions({
        apps: appsResponse.data?.apps || [],
        adUnits: adUnitsResponse.data?.ad_units || [],
        countries: countriesResponse.data?.countries || [],
        adSources: adSourcesResponse.data?.data || [],
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
      // Don't show error to user, just log it
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
      };

      if (accountId) {
        params.account_id = accountId;
      }

      // Add filters to params
      if (filters.appKey.length > 0) {
        params.appKey = filters.appKey.join(',');
      }
      if (filters.country.length > 0) {
        params.country = filters.country.join(',');
      }
      if (filters.adUnit.length > 0) {
        params.adUnit = filters.adUnit.join(',');
      }
      if (filters.adSource.length > 0) {
        params.adSource = filters.adSource.join(',');
      }

      // Fetch analytics, chart, and table data in parallel
      const [analyticsResponse, chartResponse, tableResponse] = await Promise.all([
        analyticsService.getDashboard(params),
        analyticsService.getRevenueChart({
          start_date: params.start_date,
          end_date: params.end_date,
          account_id: params.account_id,
          period: 'daily'
        }),
        analyticsService.getRevenueSummary({
          start_date: params.start_date,
          end_date: params.end_date,
          account_id: params.account_id,
          breakdown: breakdownType,
          sort_by: 'revenue',
          limit: 0 // Get all results for table
        })
      ]);

      setAnalytics(analyticsResponse.data);
      setChartData(chartResponse.data);
      setTableData(tableResponse.data);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load analytics data';
      setError(errorMessage);
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [dateRange.startDate, dateRange.endDate, accountId]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshEnabled) {
      refreshIntervalRef.current = setInterval(() => {
        fetchAnalytics();
      }, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }
  }, [autoRefreshEnabled, refreshInterval]);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange.startDate, dateRange.endDate, accountId, filters, breakdownType]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      appKey: [],
      country: [],
      adUnit: [],
      adSource: [],
    });
  };

  if (loading && !analytics) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive mb-4">{error}</p>
            <Button
              onClick={fetchAnalytics}
              variant="destructive"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const metrics = analytics?.metrics || {};
  const dateRangeInfo = analytics?.date_range || {};
  const dateRangeDisplay = dateRangeInfo.start_date && dateRangeInfo.end_date
    ? `${dateRangeInfo.start_date} - ${dateRangeInfo.end_date}`
    : `${dateRange.startDate} - ${dateRange.endDate}`;

  const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Analytics Dashboard</h1>
            <p className="text-muted-foreground text-xs">
              {analytics?.account_name && `Account: ${analytics.account_name}`}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters || hasActiveFilters ? "default" : "outline"}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-2">
                  {Object.values(filters).reduce((sum, arr) => sum + arr.length, 0)}
                </Badge>
              )}
            </Button>
          </div>
        </div>
        <AutoRefreshControl
          lastUpdated={lastUpdated}
          autoRefreshEnabled={autoRefreshEnabled}
          refreshInterval={refreshInterval}
          onToggleAutoRefresh={setAutoRefreshEnabled}
          onRefreshIntervalChange={setRefreshInterval}
          onManualRefresh={fetchAnalytics}
        />
      </div>

      {/* Date Range Picker */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle>Date Range</CardTitle>
        </CardHeader>
        <CardContent>
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={handleDateRangeChange}
          />
        </CardContent>
      </Card>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-2 sm:mb-3 lg:mb-4">
          <FilterPanel
            filters={filters}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
            onClearFilters={clearFilters}
          />
        </div>
      )}

      {/* Summary Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <SummaryCard
          title="Total Revenue"
          value={metrics.revenue || metrics.total_revenue || 0}
          subtitle={dateRangeDisplay}
          icon={DollarSign}
          isCurrency={true}
        />
        <SummaryCard
          title="Total Impressions"
          value={metrics.impressions || metrics.total_impressions || 0}
          subtitle={dateRangeDisplay}
          icon={Eye}
          isCurrency={false}
        />
        <SummaryCard
          title="Total Clicks"
          value={metrics.clicks || metrics.total_clicks || 0}
          subtitle={dateRangeDisplay}
          icon={MousePointerClick}
          isCurrency={false}
        />
        <SummaryCard
          title="Average eCPM"
          value={metrics.ecpm || metrics.average_ecpm || 0}
          subtitle={dateRangeDisplay}
          icon={TrendingUp}
          isCurrency={true}
        />
      </div>

      {/* Additional Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <SummaryCard
          title="CTR"
          value={metrics.ctr || 0}
          subtitle="Click-through rate"
          icon={Activity}
          isCurrency={false}
          isPercentage={true}
        />
        <SummaryCard
          title="Fill Rate"
          value={metrics.fill_rate || 0}
          subtitle="Fill rate percentage"
          icon={TrendingUp}
          isCurrency={false}
          isPercentage={true}
        />
        <SummaryCard
          title="Completion Rate"
          value={metrics.completion_rate || 0}
          subtitle="Video completion rate"
          icon={Activity}
          isCurrency={false}
          isPercentage={true}
        />
        <SummaryCard
          title="Daily Active Users"
          value={metrics.active_users || 0}
          subtitle="Average DAU"
          icon={Users}
          isCurrency={false}
        />
      </div>

      {/* User Engagement Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        <SummaryCard
          title="Engaged Users"
          value={metrics.engaged_users || 0}
          subtitle="Engaged users"
          icon={UserCheck}
          isCurrency={false}
        />
        <SummaryCard
          title="Sessions"
          value={metrics.sessions || 0}
          subtitle="Total sessions"
          icon={Clock}
          isCurrency={false}
        />
        <SummaryCard
          title="Impressions/Session"
          value={metrics.impressions_per_session || 0}
          subtitle="Avg impressions per session"
          icon={TrendingUp}
          isCurrency={false}
        />
      </div>

      {/* Revenue Chart */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <RevenueChart
            data={chartData?.data || []}
            type="line"
            height={350}
            showLegend={true}
          />
        </CardContent>
      </Card>

      {/* Data Table Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Detailed Breakdown</CardTitle>
            <Select
              value={breakdownType}
              onValueChange={setBreakdownType}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="app">By App</SelectItem>
                <SelectItem value="ad_unit">By Ad Unit</SelectItem>
                <SelectItem value="country">By Country</SelectItem>
                <SelectItem value="ad_source">By Ad Source</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {tableData?.data && (
            <DataTable
              data={tableData.data}
              columns={tableColumns}
              pageSize={10}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
